import React from 'react';
import { ScrollView, StyleSheet, ActivityIndicator } from 'react-native';
import { YStack } from '@tamagui/stacks';
import { Text } from '@tamagui/core';
import SectionHeader from './SectionHeader';
import ChatListItem from './ChatListItem';
import { COLORS } from '@/constants/colors';

type ChatListItemType = {
  id: string;
  odataName: string;
  odataAvatar: string;
  lastMessage: string;
  lastMessageTime: Date;
  unreadCount: number;
  verified: boolean;
  meetingInfo?: {
    date: string;
    time: string;
    location: string;
  };
};

type MessagesListProps = {
  textColor: string;
  isDark: boolean;
  onChatPress: (chatId: string) => void;
  chats: ChatListItemType[];
  isLoading: boolean;
  error: Error | null;
  selectedChatId?: string | null;
};

/**
 * Formatuj czas dla wyświetlenia w liście czatów
 */
const formatChatTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'teraz';
  if (diffMins < 60) return `${diffMins} min`;
  if (diffHours < 24) return `${diffHours} godz`;
  if (diffDays === 1) return 'wczoraj';
  if (diffDays < 7) return `${diffDays} dni`;
  
  return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
};

const MessagesList = React.memo<MessagesListProps>(({ 
  textColor, 
  isDark, 
  onChatPress,
  chats,
  isLoading,
  error,
  selectedChatId,
}) => {
  if (isLoading) {
    return (
      <YStack flex={1} justifyContent="center" alignItems="center">
        <ActivityIndicator size="large" color={COLORS.PRIMARY} />
        <Text color={textColor} marginTop="$2">Ładowanie wiadomości...</Text>
      </YStack>
    );
  }

  // Gdy brak czatów (lub błąd) - pokaż placeholder
  if (chats.length === 0) {
    return (
      <YStack flex={1}>
        <SectionHeader title="Wiadomości" textColor={textColor} />
        <YStack flex={1} justifyContent="center" alignItems="center" padding="$4">
          <Text color={textColor} textAlign="center" opacity={0.6} fontSize={16} fontWeight="600">
            Brak rozmów
          </Text>
          <Text color={textColor} textAlign="center" opacity={0.5} fontSize={14} marginTop="$3" lineHeight={22} paddingHorizontal="$4">
            Aby rozmawiać z innymi osobami, odpowiedz na zgłoszenia lub dodaj własne ogłoszenie
          </Text>
        </YStack>
      </YStack>
    );
  }

  return (
    <YStack flex={1}>
      <SectionHeader title="Wiadomości" textColor={textColor} />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.messagesList}
        contentContainerStyle={styles.scrollContent}
      >
        {chats.map((item) => (
          <ChatListItem
            key={item.id}
            imageUri={item.odataAvatar}
            name={item.odataName}
            lastMessage={item.lastMessage}
            time={formatChatTime(item.lastMessageTime)}
            unreadCount={item.unreadCount}
            verified={item.verified}
            onPress={() => onChatPress(item.id)}
            isDark={isDark}
            selected={selectedChatId === item.id}
          />
        ))}
      </ScrollView>
    </YStack>
  );
});

const styles = StyleSheet.create({
  messagesList: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 8,
  },
});

MessagesList.displayName = 'MessagesList';

export default MessagesList;

